import globalMessage from "@/utils/globalMessage";
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Form, Input, Button, Spin, Modal, Row, Col, Tag, Statistic, Divider, Timeline, Radio, Space } from "antd";
import DetailTabs from "@components/DetailTabs";
import Table from "@components/ResponsiveTable";
import queryString from "query-string";
import { api } from "@redux/api";
import RecordAuditTimeline from "@components/Table/RecordAuditLog";
import { useSelector } from "react-redux";
import moment from "moment";

import { useKeepAliveContext } from "@/hooks/use-keep-alive-context";
import ResponsiveFileManagement from "@components/FileManagement/ResponsiveFileManagement";
import RelatedWorkorder from "@containers/facility/Equipment/RelatedWorkorder/RelatedWorkorder";
import FunctionalTesting from "@containers/facility/Equipment/FunctionalTesting/FunctionalTesting";
import _ from "lodash";
import EChartsReact from "@components/EChartsReact";
import "./index.css";
import { useAuthEditable, useAuthFullAccess } from "@/hooks/use-auth";
import { EventName, emitEvent } from "@/hooks/useEventBus";
import { formatPMScheduleRuleSummary } from "../../PM/pmScheduleUtils";
import { buildFacilityPath, getFacilityContext, getLeafFromPathname } from "@/utils/route-context";
import Select from "@components/ResponsiveSelect";
import DatePicker from "@components/ResponsiveDatePicker";
import EquipmentDynamicFields from "@components/EquipmentDynamicFields";
import {
  normalizeEquipmentCategoryForms,
  normalizeEquipmentFormValuesForForm,
  normalizeEquipmentFormValuesForSubmit,
} from "@/utils/equipment-category-forms";

const { TabPane } = DetailTabs;
const { TextArea } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;


const COST_CHART_PALETTE = [
  "#3A3F63",
  "#2563eb",
  "#16a34a",
  "#ca8a04",
  "#9333ea",
  "#ea580c",
  "#64748b",
];

/** Build stacked bar option from timeline rows; uses each row's `accounts` when present. */
function buildCostTimelineChartOption(timeline) {
  if (!timeline?.length) return null;
  const periods = timeline.map((x) => x.period);
  const rowKey = (a) =>
    a.code != null && a.code !== "" ? String(a.code) : `__${a.label || "uncat"}`;

  const seriesMeta = [];
  const seen = new Set();
  for (const row of timeline) {
    for (const acc of row.accounts || []) {
      const k = rowKey(acc);
      if (!seen.has(k)) {
        seen.add(k);
        seriesMeta.push({ key: k, label: acc.label || k });
      }
    }
  }

  if (!seriesMeta.length) {
    const maint = timeline.map((x) => Number(x.maintenance_cost) || 0);
    const cap = timeline.map((x) => Number(x.capital_cost) || 0);
    return {
      backgroundColor: "#fff",
      tooltip: { trigger: "axis" },
      legend: { data: ["Maintenance", "Capital"], bottom: 0 },
      grid: { left: "3%", right: "4%", bottom: "12%", containLabel: true },
      xAxis: { type: "category", data: periods, axisLabel: { rotate: 30 } },
      yAxis: { type: "value", name: "Cost (USD)" },
      series: [
        {
          name: "Maintenance",
          type: "bar",
          stack: "cost",
          data: maint,
          itemStyle: { color: COST_CHART_PALETTE[0] },
        },
        {
          name: "Capital",
          type: "bar",
          stack: "cost",
          data: cap,
          itemStyle: { color: COST_CHART_PALETTE[1] },
        },
      ],
    };
  }

  const series = seriesMeta.map((s, idx) => ({
    name: s.label,
    type: "bar",
    stack: "cost",
    data: timeline.map((row) => {
      const list = row.accounts || [];
      const hit = list.find((a) => rowKey(a) === s.key);
      return hit ? Number(hit.total) || 0 : 0;
    }),
    itemStyle: { color: COST_CHART_PALETTE[idx % COST_CHART_PALETTE.length] },
  }));

  return {
    backgroundColor: "#fff",
    tooltip: { trigger: "axis" },
    legend: { data: seriesMeta.map((s) => s.label), bottom: 0 },
    grid: {
      left: "3%",
      right: "4%",
      bottom: seriesMeta.length > 6 ? "18%" : "12%",
      containLabel: true,
    },
    xAxis: { type: "category", data: periods, axisLabel: { rotate: 30 } },
    yAxis: { type: "value", name: "Cost (USD)" },
    series,
  };
}

/** Preset ranges for Cost tab (API uses completed date if set, else created date). */
const COST_DURATION_PRESETS = [
  {
    key: "30d",
    label: "Last 30 days",
    getRange: () => [moment().subtract(30, "days").startOf("day"), moment().endOf("day")],
  },
  {
    key: "90d",
    label: "Last 90 days",
    getRange: () => [moment().subtract(90, "days").startOf("day"), moment().endOf("day")],
  },
  {
    key: "6m",
    label: "Last 6 months",
    getRange: () => [moment().subtract(6, "months").startOf("day"), moment().endOf("day")],
  },
  {
    key: "12m",
    label: "Last 12 months",
    getRange: () => [moment().subtract(12, "months").startOf("month"), moment().endOf("day")],
  },
  {
    key: "ytd",
    label: "Year to date",
    getRange: () => [moment().startOf("year"), moment().endOf("day")],
  },
  { key: "all", label: "All time", getRange: null },
];

const EquipmentDetail = ({ isAdd = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const { closeTab, refreshTab } = useKeepAliveContext();
  const editable = useAuthEditable('equipment');
  const fullAccess = useAuthFullAccess('equipment');
  const facilityContext = useSelector((state) => getFacilityContext(state));
  const equipmentListPath = buildFacilityPath("equipment", facilityContext) || "/switch-view";

  // Determine if this is a create page from either canonical or legacy route
  const isCreatePage = isAdd || getLeafFromPathname(location.pathname, "facility") === "create-equipment";

  const [loading, setLoading] = useState(isCreatePage ? false : true);
  const [readOnly, setReadOnly] = useState(!isCreatePage);
  const [equipmentData, setEquipmentData] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [categoryMap, setCategoryMap] = useState({}); // Map category name to full record
  const [categoryById, setCategoryById] = useState({}); // Map category id -> record
  const [selectedCategoryForms, setSelectedCategoryForms] = useState([]);
  const [parentEquipmentOptions, setParentEquipmentOptions] = useState([]);
  const [parentSearchLoading, setParentSearchLoading] = useState(false);
  const parentSearchTimerRef = useRef(null);
  const [activeTabKey, setActiveTabKey] = useState("1");
  const [pmInfo, setPmInfo] = useState(null);
  const [components, setComponents] = useState(null);
  const [costSummary, setCostSummary] = useState(null);
  const [costDateRange, setCostDateRange] = useState(() => [
    moment().subtract(12, "months").startOf("month"),
    moment().endOf("day"),
  ]);
  const [costDurationPreset, setCostDurationPreset] = useState("12m");
  const [pmInfoLoading, setPmInfoLoading] = useState(false);
  const [componentsLoading, setComponentsLoading] = useState(false);
  const [costLoading, setCostLoading] = useState(false);
  const costFetchSeqRef = useRef(0);

  const userName = useSelector(state => 
    state?.Auth?.user || 
    state?.Auth?.first_name + " " + state?.Auth?.last_name
  );

  useEffect(() => {
    fetchCategoryList();
    if (!isCreatePage && id) {
      fetchEquipmentDetail();
    } else if (isCreatePage) {
      // For create mode, set initial values
      form.setFieldsValue({
        condition: 'Null'
      });
    }
  }, [id, isCreatePage]);

  // Create equipment: pre-fill parent from ?parent_equipment=
  useEffect(() => {
    if (!isCreatePage) return;
    const q = queryString.parse(location.search);
    const raw = q.parent_equipment;
    if (raw === undefined || raw === null || raw === "") return;
    const pid = parseInt(String(raw), 10);
    if (Number.isNaN(pid)) return;
    form.setFieldsValue({ parent_equipment: pid });
    api
      .filterEquipment(`id=${pid}`)
      .then((res) => {
        const row = res.data?.results?.[0];
        if (row) {
          setParentEquipmentOptions([{ value: row.id, label: row.name }]);
        }
      })
      .catch(() => {});
  // Form instance is stable; omitting `form` avoids unnecessary effect reruns.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreatePage, location.search]);

  useEffect(() => {
    if (equipmentData) {
      setSelectedRecord(equipmentData);
    }
  }, [equipmentData]);

  // When category list loads after equipment, sync category name + equipment_category FK
  useEffect(() => {
    if (!equipmentData || Object.keys(categoryById).length === 0) return;
    const catId = equipmentData.equipment_category;
    let name = equipmentData.category;
    if (catId != null && categoryById[catId]) {
      name = categoryById[catId].name;
      const categoryForms = normalizeEquipmentCategoryForms(categoryById[catId].forms);
      setSelectedCategoryForms(categoryForms);
      form.setFieldsValue({
        form_values: normalizeEquipmentFormValuesForForm(
          categoryForms,
          equipmentData.form_values
        ),
      });
    }
    form.setFieldsValue({
      category: name,
      equipment_category: catId != null ? catId : undefined,
    });
    // Form instance is stable; omitting `form` avoids unnecessary effect reruns.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipmentData, categoryById]);

  // Fetch category list
  const fetchCategoryList = async () => {
    try {
      const response = await api.filterEquipmentCategory('page=1&page_size=999');
      if (response.status === 200 && response.data.results) {
        const categories = response.data.results;
        const options = categories.map(cat => ({
          label: cat.name,
          value: cat.name,
        }));
        setCategoryOptions(options);

        // Create a map for quick lookup
        const map = {};
        const byId = {};
        categories.forEach(cat => {
          map[cat.name] = cat;
          byId[cat.id] = cat;
        });
        setCategoryMap(map);
        setCategoryById(byId);
      }
    } catch (error) {
      console.error("Error fetching category list:", error);
    }
  };

  // Handle category change and reset values that belong to the previous category.
  const handleCategoryChange = (categoryName) => {
    const categoryRecord = categoryMap[categoryName];
    if (categoryRecord) {
      const categoryForms = normalizeEquipmentCategoryForms(categoryRecord.forms);
      setSelectedCategoryForms(categoryForms);
      form.setFieldsValue({ equipment_category: categoryRecord.id });
      form.setFieldsValue({
        form_values: normalizeEquipmentFormValuesForForm(categoryForms, {}),
      });
    }
  };

  const searchParentEquipments = useCallback(
    async (keyword) => {
      if (!keyword || !keyword.trim()) return;
      setParentSearchLoading(true);
      try {
        const res = await api.lookupEquipments(encodeURIComponent(keyword.trim()));
        const list = res.data || [];
        let opts = list.map((e) => ({ value: e.id, label: e.name }));
        if (id && !isCreatePage) {
          opts = opts.filter((o) => String(o.value) !== String(id));
        }
        setParentEquipmentOptions(opts);
      } catch (e) {
        message.error("Failed to search parent equipment");
      } finally {
        setParentSearchLoading(false);
      }
    },
    [id, isCreatePage]
  );

  const onParentEquipmentSearch = (value) => {
    if (parentSearchTimerRef.current) {
      clearTimeout(parentSearchTimerRef.current);
    }
    parentSearchTimerRef.current = setTimeout(() => {
      searchParentEquipments(value);
    }, 300);
  };

  const fetchEquipmentDetail = async () => {
    try {
      setLoading(true);
      const response = await api.filterEquipment(`id=${id}`);

      if (response.status === 200 && response.data.results.length > 0) {
        const data = response.data.results[0];
        setEquipmentData(data);

        // Set form values with date conversion
        const formValues = {
          ...data,
          install_date: data.install_date ? moment(data.install_date) : null,
          warranty_expiry: data.warranty_expiry ? moment(data.warranty_expiry) : null,
          created_date: data.created_date ? moment(data.created_date).format("YYYY-MM-DD HH:mm") : "",
          modified_date: data.modified_date ? moment(data.modified_date).format("YYYY-MM-DD HH:mm") : "",
          last_condition_update: data.last_condition_update ? moment(data.last_condition_update).format("YYYY-MM-DD HH:mm") : "",
        };
        form.setFieldsValue(formValues);

        // Parent equipment label for Select
        if (data.parent_equipment) {
          try {
            const pres = await api.filterEquipment(`id=${data.parent_equipment}`);
            const prow = pres.data?.results?.[0];
            if (prow) {
              setParentEquipmentOptions([{ value: prow.id, label: prow.name }]);
            } else {
              setParentEquipmentOptions([
                { value: data.parent_equipment, label: `Equipment #${data.parent_equipment}` },
              ]);
            }
          } catch {
            setParentEquipmentOptions([
              { value: data.parent_equipment, label: `Equipment #${data.parent_equipment}` },
            ]);
          }
        } else {
          setParentEquipmentOptions([]);
        }
      } else {
        message.error("Equipment not found");
      }
    } catch (error) {
      console.error("Error fetching equipment:", error);
      message.error("Failed to load equipment details");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setReadOnly(false);
  };

  const handleCancel = () => {
    setReadOnly(true);
    if (equipmentData) {
      const formValues = {
        ...equipmentData,
        install_date: equipmentData.install_date ? moment(equipmentData.install_date) : null,
        warranty_expiry: equipmentData.warranty_expiry ? moment(equipmentData.warranty_expiry) : null,
        created_date: equipmentData.created_date ? moment(equipmentData.created_date).format("YYYY-MM-DD HH:mm") : "",
        modified_date: equipmentData.modified_date ? moment(equipmentData.modified_date).format("YYYY-MM-DD HH:mm") : "",
        last_condition_update: equipmentData.last_condition_update ? moment(equipmentData.last_condition_update).format("YYYY-MM-DD HH:mm") : "",
      };
      const categoryRecord = categoryById[equipmentData.equipment_category];
      const categoryForms = normalizeEquipmentCategoryForms(categoryRecord?.forms);
      setSelectedCategoryForms(categoryForms);
      formValues.form_values = normalizeEquipmentFormValuesForForm(
        categoryForms,
        equipmentData.form_values
      );
      form.setFieldsValue(formValues);
    }
  };

  const stripReadOnlySubmitFields = (obj) => {
    const omit = [
      "createdby_name",
      "lastModified_name",
      "facility_name",
      "outstanding_workers",
      "recent_workorders_count",
      "recent_workorders_notcompleted_count",
      "recent_functionform",
      "tags_list",
      "equipment_score",
      "equipment_age_score",
      "visual_inspection_score",
      "deficiency_score",
      "last_condition_update",
      "created_date",
      "modified_date",
    ];
    for (let i = 1; i <= 8; i += 1) {
      omit.push(`key_spec_${i}_name`, `key_spec_${i}_value`);
    }
    const next = { ...obj };
    omit.forEach((k) => delete next[k]);
    return next;
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      // CMMS v2: sync equipment_category FK from selected category name if missing
      let equipmentCategoryId = values.equipment_category;
      if (typeof equipmentCategoryId === "string" && equipmentCategoryId !== "") {
        equipmentCategoryId = parseInt(equipmentCategoryId, 10);
      }
      if (
        (equipmentCategoryId === undefined ||
          equipmentCategoryId === null ||
          Number.isNaN(equipmentCategoryId)) &&
        values.category &&
        categoryMap[values.category]
      ) {
        equipmentCategoryId = categoryMap[values.category].id;
      }

      // Format dates before submitting
      const submitValues = stripReadOnlySubmitFields({
        ...values,
        install_date: values.install_date ? values.install_date.format("YYYY-MM-DD") : null,
        warranty_expiry: values.warranty_expiry ? values.warranty_expiry.format("YYYY-MM-DD") : null,
        form_values: normalizeEquipmentFormValuesForSubmit(
          selectedCategoryForms,
          values.form_values
        ),
        equipment_category:
          equipmentCategoryId != null && !Number.isNaN(equipmentCategoryId)
            ? equipmentCategoryId
            : null,
        parent_equipment: (() => {
          let pe = values.parent_equipment;
          if (pe === undefined || pe === null || pe === "") return null;
          if (typeof pe === "string") pe = parseInt(pe, 10);
          return Number.isNaN(pe) ? null : pe;
        })(),
      });

      delete submitValues.id;

      let response;
      if (isCreatePage) {
        // Create mode
        response = await api.createEquipment(submitValues);
      } else {
        // Edit mode
        response = await api.editEquipment(id, submitValues);
      }

      if (response.status === 200 || response.status === 201) {
        globalMessage.success({
          message: "Success",
          description: isCreatePage ? "Equipment created successfully" : "Equipment updated successfully",
        });

        // Emit event to refresh equipment list
        emitEvent(EventName.REFRESH_EQUIPMENT_LIST);

        if (isCreatePage) {
          // Refresh Equipment list page and close current tab
          refreshTab(equipmentListPath);
          closeTab();
          navigate(equipmentListPath);
        } else {
          setReadOnly(true);
          fetchEquipmentDetail();
        }
      } else {
        globalMessage.error({
          message: "Error",
          description: isCreatePage ? "Failed to create equipment" : "Failed to update equipment",
        });
      }
    } catch (error) {
      console.error(`Error ${isCreatePage ? 'creating' : 'updating'} equipment:`, error);
      globalMessage.error({
        message: "Error",
        description: isCreatePage ? "Failed to create equipment" : "Failed to update equipment",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    Modal.confirm({
      title: "Are you sure you want to delete this equipment?",
      content: "This action cannot be undone.",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          setLoading(true);
          const response = await api.deleteEquipment(id);

          if (response.status === 204 || response.status === 200) {
            globalMessage.success({
              message: "Success",
              description: "Equipment deleted successfully",
            });
            // Refresh Equipment list page and close current tab
            refreshTab(equipmentListPath);
            closeTab();
            navigate(equipmentListPath);
          } else {
            globalMessage.error({
              message: "Error",
              description: "Failed to delete equipment",
            });
          }
        } catch (error) {
          console.error("Error deleting equipment:", error);
          globalMessage.error({
            message: "Error",
            description: "Failed to delete equipment",
          });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleCancelCreate = () => {
    closeTab();
    navigate(equipmentListPath);
  };

  const renderOperationButton = () => {
    if (isCreatePage) {
      return (
        <>
          <Button onClick={handleCancelCreate}>
            <i className="fa-solid fa-xmark"></i>Cancel
          </Button>
          <Button type="primary" onClick={() => form.submit()}>
            <i className="fa-solid fa-floppy-disk"></i>Save
          </Button>
        </>
      );
    }

    if (readOnly) {
      return (
        <>
         {editable &&  <Button type="primary" onClick={handleEdit}>
            <i className="fa-solid fa-pen-to-square"></i>Edit
          </Button>}
          {fullAccess && <Button danger onClick={handleDelete}>
            <i className="fa-solid fa-trash"></i>Delete
          </Button>}
        </>
      );
    } else {
      return (
        <>
          <Button type="primary" onClick={() => form.submit()}>
            <i className="fa-solid fa-floppy-disk"></i>Save
          </Button>
          <Button onClick={handleCancel}>
            Cancel
          </Button>
        </>
      );
    }
  };



  const loadPMInfo = async () => {
    if (pmInfo || pmInfoLoading || !id) return;
    try {
      setPmInfoLoading(true);
      const res = await api.getEquipmentPMInfo(id);
      if (res.status === 200) setPmInfo(res.data);
    } catch {
      message.error("Failed to load PM information");
    } finally {
      setPmInfoLoading(false);
    }
  };

  const loadComponents = async () => {
    if (components !== null || componentsLoading || !id) return;
    try {
      setComponentsLoading(true);
      const res = await api.getEquipmentComponents(id);
      if (res.status === 200) setComponents(Array.isArray(res.data) ? res.data : res.data.results || []);
    } catch {
      message.error("Failed to load components");
    } finally {
      setComponentsLoading(false);
    }
  };

  const loadCostSummary = async (rangeOverride = null, options = {}) => {
    if (!id) return;

    if (!options.allTime) {
      const range = rangeOverride || costDateRange;
      if (!range?.[0] || !range?.[1]) {
        message.warning("Please select a complete date range");
        return;
      }
    }

    costFetchSeqRef.current += 1;
    const seq = costFetchSeqRef.current;

    if (options.allTime) {
      try {
        setCostLoading(true);
        const res = await api.getEquipmentCostSummary(id, {});
        if (seq !== costFetchSeqRef.current) return;
        if (res.status === 200) setCostSummary(res.data);
      } catch {
        if (seq !== costFetchSeqRef.current) return;
        message.error("Failed to load cost summary");
      } finally {
        if (seq === costFetchSeqRef.current) setCostLoading(false);
      }
      return;
    }
    const range = rangeOverride || costDateRange;
    try {
      setCostLoading(true);
      const res = await api.getEquipmentCostSummary(id, {
        start_date: range[0].format("YYYY-MM-DD"),
        end_date: range[1].format("YYYY-MM-DD"),
      });
      if (seq !== costFetchSeqRef.current) return;
      if (res.status === 200) setCostSummary(res.data);
    } catch {
      if (seq !== costFetchSeqRef.current) return;
      message.error("Failed to load cost summary");
    } finally {
      if (seq === costFetchSeqRef.current) setCostLoading(false);
    }
  };

  const applyCostDurationPreset = (presetKey) => {
    setCostDurationPreset(presetKey);
    if (presetKey === "all") {
      loadCostSummary(null, { allTime: true });
      return;
    }
    const preset = COST_DURATION_PRESETS.find((p) => p.key === presetKey);
    if (!preset || !preset.getRange) return;
    const r = preset.getRange();
    setCostDateRange(r);
    loadCostSummary(r);
  };

  const costChartOption = useMemo(() => {
    const tl = (costSummary && costSummary.timeline) || [];
    return buildCostTimelineChartOption(tl);
  }, [costSummary]);

  const handleCostDateRangeChange = (dates) => {
    setCostDurationPreset("custom");
    setCostDateRange(dates);
    if (dates && dates[0] && dates[1] && id) {
      loadCostSummary(dates);
    }
  };

  const handleTabChange = (key) => {
    setActiveTabKey(key);
    if (key === "pm") loadPMInfo();
    else if (key === "components") loadComponents();
    else if (key === "cost") {
      if (costDurationPreset === "all") loadCostSummary(null, { allTime: true });
      else loadCostSummary();
    }
  };

  if (loading && !equipmentData && !isCreatePage) {
    return (
      <div style={{ textAlign: "center", padding: "100px 0" }}>
        <Spin size="large" />
      </div>
    );
  }

    return (
    <div className="equipment-detail-container">
      <Spin spinning={loading}>
        <DetailTabs
          defaultActiveKey="1" 
          destroyInactiveTabPane={true}
          activeKey={activeTabKey}
          onChange={handleTabChange}
          tabBarExtraContent={activeTabKey === "1" ? renderOperationButton() : null}
        >
          <TabPane
            tab={
              <span>
                <i className="fa-regular fa-rectangle-list pr-2"></i>Detail
              </span>
            }
            key="1"
          >
            <div>
              <div className="slds-m-around_x-small">
                <div className="px-4">
                  <div className="w-full">
                    <Form
                      form={form}
                      onFinish={handleSubmit}
                      layout="horizontal"
                      labelCol={{ span: 8 }}
                      wrapperCol={{ span: 16 }}
                      className="detailForm"
                      // size="small"
                        autoComplete="off">
                      <Row gutter={[0, 0]}>
                        {/* ID - Hidden in create mode */}
                        {!isCreatePage && (
                          <>
                            <Col xs={24} lg={12}>
                              <Form.Item label="ID" name="id">
                                <Input disabled />
                              </Form.Item>
                            </Col>
                            <Col xs={0} lg={12} />
                          </>
                        )}

                        {/* Name */}
                        <Col xs={24} lg={12}>
                          <Form.Item 
                            label="Name" 
                            name="name"
                            rules={[{ required: true, message: "This field is required" }]}
                          >
                            <Input disabled={readOnly} />
                          </Form.Item>
                        </Col>

                        {/* Parent Equipment — CMMS v2 (sub-asset / component) */}
                        <Col xs={24} lg={12}>
                          <Form.Item label="Parent Equipment" name="parent_equipment">
                            {readOnly ? (
                              <Select
                                disabled
                                allowClear
                                placeholder="None"
                                options={parentEquipmentOptions}
                              />
                            ) : (
                              <Select
                                showSearch
                                allowClear
                                filterOption={false}
                                onSearch={onParentEquipmentSearch}
                                options={parentEquipmentOptions}
                                loading={parentSearchLoading}
                                placeholder="Search equipment by name"
                                notFoundContent={parentSearchLoading ? <Spin size="small" /> : "Type to search"}
                              />
                            )}
                          </Form.Item>
                        </Col>

                        {/* CMMS v2: FK for PMChecklist matching (synced from Category) */}
                        <Form.Item name="equipment_category" hidden>
                          <Input />
                        </Form.Item>

                        {/* Category */}
                        <Col xs={24} lg={12}>
                          <Form.Item 
                            label="Category" 
                            name="category"
                            rules={[{ required: true, message: "This field is required" }]}
                          >
                            {readOnly ? (
                              <Input disabled value={equipmentData?.category || ""} />
                            ) : (
                              <Select 
                                disabled={readOnly}
                                placeholder="Select Category"
                                onChange={handleCategoryChange}
                                showSearch
                                filterOption={(input, option) =>
                                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                options={categoryOptions}
                              />
                            )}
                          </Form.Item>
                        </Col>

                        {/* Service */}
                        <Col xs={24} lg={12}>
                          <Form.Item label="Service" name="service">
                            <Input disabled={readOnly} />
                          </Form.Item>
                        </Col>

                        {/* Condition */}
                        <Col xs={24} lg={12}>
                          <Form.Item 
                            label="Condition" 
                            name="condition"
                            rules={[{ required: true, message: "This field is required" }]}
                            initialValue="Null"
                          >
                            <Select disabled={readOnly}>
                              <Option value="Null">Null</Option>
                              <Option value="Excellent">Excellent</Option>
                              <Option value="Good">Good</Option>
                              <Option value="Fair">Fair</Option>
                              <Option value="Poor">Poor</Option>
                              <Option value="Failed">Failed</Option>
                            </Select>
                          </Form.Item>
                        </Col>

                        {/* Building */}
                        <Col xs={24} lg={12}>
                          <Form.Item label="Building" name="building">
                            <Input disabled={readOnly} />
                          </Form.Item>
                        </Col>

                        {/* Location */}
                        <Col xs={24} lg={12}>
                          <Form.Item label="Location" name="location">
                            <Input disabled={readOnly} />
                          </Form.Item>
                        </Col>

                        {/* Install Date */}
                        <Col xs={24} lg={12}>
                          <Form.Item label="Install Date" name="install_date">
                            <DatePicker format="YYYY-MM-DD" style={{ width: "100%" }} disabled={readOnly} />
                          </Form.Item>
                        </Col>

                        {/* Manufacturer */}
                        <Col xs={24} lg={12}>
                          <Form.Item label="Manufacturer" name="Manufacturer">
                            <Input disabled={readOnly} />
                          </Form.Item>
                        </Col>

                        {/* Equipment Model */}
                        <Col xs={24} lg={12}>
                          <Form.Item label="Equipment Model" name="Equipment_Model">
                            <Input disabled={readOnly} />
                          </Form.Item>
                        </Col>

                        {/* Equipment SN */}

                        <Col xs={24} lg={24}></Col>
                        {/* Description - Full Width */}
                        <Col xs={24} lg={12}>
                          <Form.Item 
                            label="Description" 
                            name="description" 
                          >
                            <TextArea rows={3} disabled={readOnly} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} lg={12}>
                          <Form.Item label="Equipment SN" name="Equipment_SN">
                            <Input disabled={readOnly} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} lg={24}></Col>
                        {/* CMMS v2: QR, warranty, service contact */}
                        <Col xs={24} lg={12}>
                          <Form.Item label="QR Code" name="qr_code">
                            <Input disabled={readOnly} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} lg={12}>
                          <Form.Item label="Barcode" name="barcode">
                            <Input disabled={readOnly} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} lg={12}>
                          <Form.Item label="Warranty Expiry" name="warranty_expiry">
                            <DatePicker format="YYYY-MM-DD" style={{ width: "100%" }} disabled={readOnly} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} lg={12}>
                          <Form.Item label="Warranty Info" name="warranty_info">
                            <TextArea rows={2} disabled={readOnly} placeholder="Warranty terms, provider, etc." />
                          </Form.Item>
                        </Col>
                        <Col xs={24} lg={12}>
                          <Form.Item label="Service Contact" name="service_contact">
                            <TextArea rows={2} disabled={readOnly} placeholder="Service provider contact" />
                          </Form.Item>
                        </Col>
                        <EquipmentDynamicFields
                          forms={selectedCategoryForms}
                          readOnly={readOnly}
                        />

                        {/* Equipment Score - Hidden in create mode */}
                        {!isCreatePage && (
                          <Col xs={24} lg={12}>
                            <Form.Item label="Equipment Score" name="equipment_score">
                              <Input disabled />
                            </Form.Item>
                          </Col>
                        )}

                        {/* Equipment Age Score - Hidden in create mode */}
                        {!isCreatePage && (
                          <Col xs={24} lg={12}>
                            <Form.Item label="Equipment Age Score" name="equipment_age_score">
                              <Input disabled />
                            </Form.Item>
                          </Col>
                        )}

                        {/* Visual Inspection Score - Hidden in create mode */}
                        {!isCreatePage && (
                          <Col xs={24} lg={12}>
                            <Form.Item label="Visual Inspection Score" name="visual_inspection_score">
                              <Input disabled />
                            </Form.Item>
                          </Col>
                        )}

                        {/* Deficiency Score - Hidden in create mode */}
                        {!isCreatePage && (
                          <Col xs={24} lg={12}>
                            <Form.Item label="Deficiency Score" name="deficiency_score">
                              <Input disabled />
                            </Form.Item>
                          </Col>
                        )}

                        {/* Last Condition Update - Hidden in create mode */}
                        {!isCreatePage && (
                          <Col xs={24} lg={12}>
                            <Form.Item label="Last Condition Update" name="last_condition_update">
                              <Input disabled />
                            </Form.Item>
                          </Col>
                        )}

                        {/* Audit fields - Hidden in create mode */}
                        {!isCreatePage && (
                          <Col xs={24} className="equipment-audit-fields">
                            <Row gutter={[0, 0]}>
                              <Col xs={24} lg={12}>
                                <Form.Item label="Created By" name="createdby_name">
                                  <span>{equipmentData?.createdby_name}</span>
                                </Form.Item>
                              </Col>
                              <Col xs={24} lg={12}>
                                <Form.Item label="Created Date" name="created_date">
                                  <span>{equipmentData?.created_date ? moment(equipmentData.created_date).format("YYYY-MM-DD HH:mm") : ""}</span>
                                </Form.Item>
                              </Col>
                              <Col xs={24} lg={12}>
                                <Form.Item label="Modified By" name="lastModified_name">
                                  <span>{equipmentData?.lastModified_name}</span>
                                </Form.Item>
                              </Col>
                              <Col xs={24} lg={12}>
                                <Form.Item label="Modified Date" name="modified_date">
                                  <span>{equipmentData?.modified_date ? moment(equipmentData.modified_date).format("YYYY-MM-DD HH:mm") : ""}</span>
                                </Form.Item>
                              </Col>
                            </Row>
                          </Col>
                        )}
                      </Row>
                    </Form>
                  </div>
                </div>
              </div>
            </div>
          </TabPane>

          {/* Related, Attachments, Forms and Log tabs - Hidden in create mode */}
          {!isCreatePage && (
            <>
              <TabPane
                tab={
                  <span>
                    <i className="fa-solid fa-table-list pr-2"></i>Related
                  </span>
                }
                key="2"
              >
                <div>
                  {id ? (
                    <RelatedWorkorder
                      relatedList={true}
                      relatedId={id}
                      parentEquipment={id}
                      relatedObject="equipment"
                    />
                  ) : null}
                </div>
              </TabPane>

              <TabPane
                tab={
                  <span>
                    <i className="fa-solid fa-file-pdf pr-2"></i>Attachments
                  </span>
                }
                key="3"
              >
                {equipmentData ? (
                  <ResponsiveFileManagement
                    tableName="equipment"
                    recordId={id}
                    equipmentId={id}
                  />
                ) : (
                  ""
                )}
              </TabPane>

              <TabPane
                tab={
                  <span>
                    <i className="fa-solid fa-file-alt pr-2"></i>Forms
                  </span>
                }
                key="4"
              >
        <div>
                  {selectedRecord ? (
                    <FunctionalTesting selectedRecord={selectedRecord} />
                  ) : null}
        </div>
              </TabPane>

              <TabPane
                tab={
                  <span>
                    <i className="fa-solid fa-timeline pr-2"></i>Log
                  </span>
                }
                key="5"
              >
                <div className="px-4">
                  <RecordAuditTimeline moduleId={id} module="Equipment" />
                </div>
              </TabPane>

              {/* v2.0: PM Tab */}
              <TabPane
                tab={
                  <span>
                    <i className="fa-solid fa-clipboard-check pr-2"></i>PM
                  </span>
                }
                key="pm"
              >
                <div style={{ padding: "16px" }}>
                  {pmInfoLoading ? (
                    <div style={{ textAlign: "center", padding: 40 }}><Spin /></div>
                  ) : pmInfo ? (
                    <>
                      <div style={{ marginBottom: 16, fontWeight: 600, fontSize: 15 }}>
                        Preventive Maintenance ({(pmInfo.pm_plans || []).length})
                      </div>
                      <Table
                        rowKey="id"
                        size="small"
                        dataSource={pmInfo.pm_plans || []}
                        pagination={false}
                        columns={[
                          { title: "Plan Name", dataIndex: "name", key: "name" },
                          {
                            title: "Schedule",
                            key: "schedule",
                            render: (_, rec) => (
                              <span>
                                {rec.cycle_type ? (
                                  <Tag color="cyan">{formatPMScheduleRuleSummary(rec)}</Tag>
                                ) : (
                                  "-"
                                )}
                              </span>
                            ),
                          },
                          {
                            title: "Next schedule date",
                            key: "next_schedule_date",
                            render: (_, rec) => {
                              const d = rec.next_schedule_date;
                              return d ? <Tag color={moment(d).isBefore(moment(), "day") ? "red" : "blue"}>{d}</Tag> : "-";
                            },
                          },
                          {
                            title: "Status",
                            dataIndex: "is_active",
                            key: "is_active",
                            render: (v) => v ? <Tag color="green">Active</Tag> : <Tag>Inactive</Tag>,
                          },
                        ]}
                      />
                      <Divider />
                      <div style={{ marginBottom: 8, fontWeight: 600, fontSize: 15 }}>
                        PM History ({(pmInfo.pm_workorders || []).length})
                      </div>
                      <Table
                        rowKey="id"
                        size="small"
                        dataSource={pmInfo.pm_workorders || []}
                        pagination={{ pageSize: 10 }}
                        columns={[
                          {
                            title: "Issue",
                            dataIndex: "issue",
                            key: "issue",
                            render: (text, rec) => (
                              <a onClick={() => navigate(buildFacilityPath(`workorder-detail/${rec.id}`, facilityContext) || equipmentListPath)}>{text}</a>
                            ),
                          },
                          {
                            title: "Status",
                            dataIndex: "status",
                            key: "status",
                            render: (v) => <Tag>{v}</Tag>,
                          },
                          {
                            title: "Created",
                            dataIndex: "created_date",
                            key: "created_date",
                            render: (v) => (v ? moment(v).format("YYYY-MM-DD HH:mm") : "-"),
                          },
                          {
                            title: "Completed",
                            dataIndex: "complete_date",
                            key: "complete_date",
                            render: (v) => v || "-",
                          },
                          {
                            title: "Total Cost",
                            dataIndex: "total_cost",
                            key: "total_cost",
                            render: (v) => v ? `$${parseFloat(v).toFixed(2)}` : "-",
                          },
                        ]}
                      />
                    </>
                  ) : (
                    <div style={{ textAlign: "center", color: "#aaa", padding: 40 }}>
                      Click to load PM information
                    </div>
                  )}
                </div>
              </TabPane>

              {/* v2.0: Components (Sub-equipment) Tab */}
              <TabPane
                tab={
                  <span>
                    <i className="fa-solid fa-sitemap pr-2"></i>Components
                  </span>
                }
                key="components"
              >
                <div style={{ padding: "16px" }}>
                  {editable && id && (
                    <Button
                      type="primary"
                      style={{ marginBottom: 16 }}
                      onClick={() => navigate(buildFacilityPath("create-equipment", {
                        ...facilityContext,
                        query: { parent_equipment: id },
                      }) || equipmentListPath)}
                    >
                      <i className="fa-solid fa-plus pr-2"></i>
                      Add Component
                    </Button>
                  )}
                  {componentsLoading ? (
                    <div style={{ textAlign: "center", padding: 40 }}><Spin /></div>
                  ) : components !== null ? (
                    <Table
                      rowKey="id"
                      size="small"
                      dataSource={components}
                      pagination={false}
                      columns={[
                        {
                          title: "Name",
                          dataIndex: "name",
                          key: "name",
                          render: (text, rec) => (
                            <a onClick={() => navigate(buildFacilityPath(`equipment-detail/${rec.id}`, facilityContext) || equipmentListPath)}>{text}</a>
                          ),
                        },
                        { title: "Status", dataIndex: "status", key: "status", render: (v) => v ? <Tag>{v}</Tag> : "-" },
                        { title: "Condition", dataIndex: "condition", key: "condition", render: (v) => v ? <Tag color="blue">{v}</Tag> : "-" },
                        { title: "Location", dataIndex: "location", key: "location" },
                        { title: "Install Date", dataIndex: "install_date", key: "install_date" },
                      ]}
                      locale={{ emptyText: "No sub-components found" }}
                    />
                  ) : (
                    <div style={{ textAlign: "center", color: "#aaa", padding: 40 }}>
                      Click to load sub-components
                    </div>
                  )}
                </div>
              </TabPane>

              {/* v2.0: Cost Summary Tab */}
              <TabPane
                tab={
                  <span>
                    <i className="fa-solid fa-dollar-sign pr-2"></i>Cost
                  </span>
                }
                key="cost"
              >
                <div style={{ padding: "16px" }}>
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ color: "#666", marginBottom: 8 }}>
                      Date range uses each work order&apos;s <strong>Completed date</strong> when set; otherwise
                      <strong>Created date</strong> (same rule as the API). All time includes every work order.
                    </div>
                    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                      <Radio.Group
                        value={COST_DURATION_PRESETS.some((p) => p.key === costDurationPreset) ? costDurationPreset : undefined}
                        onChange={(e) => applyCostDurationPreset(e.target.value)}
                        buttonStyle="solid"
                        size="small"
                      >
                        {COST_DURATION_PRESETS.map((p) => (
                          <Radio.Button key={p.key} value={p.key}>
                            {p.label}
                          </Radio.Button>
                        ))}
                      </Radio.Group>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
                        <span style={{ color: "#666" }}>Custom range</span>
                        <RangePicker
                          value={costDurationPreset === "all" ? null : costDateRange}
                          onChange={handleCostDateRangeChange}
                          allowClear={costDurationPreset === "all"}
                          style={{ minWidth: 280 }}
                        />
                      </div>
                      {costDurationPreset === "all" ? (
                        <div style={{ color: "#888", fontSize: 12 }}>
                          Chart and monthly timeline require a date range. All-time totals include every work order.
                        </div>
                      ) : (
                        <div style={{ color: "#888", fontSize: 12 }}>
                          If ranged totals are $0, check that work order <strong>created</strong> or{" "}
                          <strong>completed</strong> dates fall in this range.
                        </div>
                      )}
                    </Space>
                  </div>
                  {costLoading ? (
                    <div style={{ textAlign: "center", padding: 40 }}><Spin /></div>
                  ) : costSummary ? (
                    <>
                      <Row gutter={24}>
                        <Col xs={24} sm={8}>
                          <Statistic
                            title="Maintenance Cost"
                            value={parseFloat(costSummary.maintenance_cost || 0).toFixed(2)}
                            prefix="$"
                            valueStyle={{ color: "#3A3F63" }}
                          />
                        </Col>
                        <Col xs={24} sm={8}>
                          <Statistic
                            title="Capital Cost"
                            value={parseFloat(costSummary.capital_cost || 0).toFixed(2)}
                            prefix="$"
                            valueStyle={{ color: "#2563eb" }}
                          />
                        </Col>
                        <Col xs={24} sm={8}>
                          <Statistic
                            title="Total Cost (incl. sub-components)"
                            value={parseFloat(costSummary.total_cost || 0).toFixed(2)}
                            prefix="$"
                            valueStyle={{ color: "#dc2626", fontWeight: 700 }}
                          />
                        </Col>
                      </Row>
                      {(costSummary.accounts && costSummary.accounts.length > 0) ? (
                        <>
                          <Divider orientation="left">By cost account</Divider>
                          <Table
                            size="small"
                            pagination={false}
                            rowKey={(r, i) => `${r.code ?? "null"}-${r.label}-${i}`}
                            dataSource={costSummary.accounts}
                            style={{ marginBottom: 24, maxWidth: 720 }}
                            columns={[
                              { title: "Account", dataIndex: "label", key: "label" },
                              {
                                title: "Code",
                                dataIndex: "code",
                                key: "code",
                                width: 120,
                                render: (c) => (c == null || c === "" ? "—" : c),
                              },
                              {
                                title: "Amount",
                                dataIndex: "total",
                                key: "total",
                                align: "right",
                                render: (t) => `$${Number(t).toFixed(2)}`,
                              },
                            ]}
                          />
                        </>
                      ) : null}
                      <Divider orientation="left">Monthly cost trend</Divider>
                      {costChartOption ? (
                        <div style={{ height: 320, marginBottom: 24 }}>
                          <EChartsReact option={costChartOption} style={{ height: "100%", width: "100%" }} />
                        </div>
                      ) : (
                        <div style={{ color: "#999", marginBottom: 24 }}>
                          No monthly breakdown for this selection (choose a date range or use a preset other than All time).
                        </div>
                      )}
                      {(costSummary.timeline && costSummary.timeline.length > 0) ? (
                        <>
                          <Divider orientation="left">Timeline</Divider>
                          <Timeline mode="left" style={{ marginTop: 8 }}>
                            {[...costSummary.timeline].reverse().map((row) => (
                              <Timeline.Item key={row.period} label={row.period}>
                                <div>
                                  <div style={{ marginBottom: row.accounts?.length ? 8 : 0 }}>
                                    <span style={{ fontWeight: 600 }}>
                                      Total ${Number(row.total_cost || 0).toFixed(2)}
                                    </span>
                                    <span style={{ color: "#666", marginLeft: 8 }}>
                                      (Maint ${Number(row.maintenance_cost || 0).toFixed(2)} · Cap $
                                      {Number(row.capital_cost || 0).toFixed(2)})
                                    </span>
                                  </div>
                                  {row.accounts && row.accounts.length > 0 ? (
                                    <Table
                                      size="small"
                                      pagination={false}
                                      rowKey={(r, i) => `${row.period}-${r.code ?? "x"}-${i}`}
                                      dataSource={row.accounts}
                                      columns={[
                                        { title: "Account", dataIndex: "label", key: "label", width: "40%" },
                                        {
                                          title: "Code",
                                          dataIndex: "code",
                                          key: "code",
                                          width: "25%",
                                          render: (c) => (c == null || c === "" ? "—" : c),
                                        },
                                        {
                                          title: "Amount",
                                          dataIndex: "total",
                                          key: "total",
                                          align: "right",
                                          render: (t) => `$${Number(t).toFixed(2)}`,
                                        },
                                      ]}
                                    />
                                  ) : null}
                                </div>
                              </Timeline.Item>
                            ))}
                          </Timeline>
                        </>
                      ) : null}
                    </>
                  ) : (
                    <div style={{ textAlign: "center", color: "#aaa", padding: 40 }}>
                      Loading or no data yet
                    </div>
                  )}
                </div>
              </TabPane>
            </>
          )}
        </DetailTabs>
      </Spin>
    </div>
  );
};

export default EquipmentDetail;
